import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {catchError, map, mergeMap, tap} from 'rxjs/operators';
import { of } from 'rxjs';
import { loadFirmaFailed, loadFirmaStart, loadFirmaSucceeded } from '../actions/getFirma.actions';
import {MitarbeiterService} from '../../mitarbeiter.service';
import {addMitarbeiterFailed, addMitarbeiterStart, addMitarbeiterSucceeded} from '../actions/mitarbeiter-add.actions';
import {FirmaService} from '../../firma.service';
import {editFirmaFailed, editFirmaStart, editFirmaSucceeded} from '../actions/firma-edit.actions';
import {
  loadFirmaZeitenFailed,
  loadFirmaZeitenStart,
  loadFirmaZeitenSucceeded
} from '@src/app/pages/Mitarbeiterverwaltung/store/actions/getFirmaZeiten.actions';
import {
  addFirmaZeitFailed,
  addFirmaZeitStart,
  addFirmaZeitSucceeded
} from '@src/app/pages/Mitarbeiterverwaltung/store/actions/firmaZeiten-add.actions';
import {
  deleteFirmaZeitFailed,
  deleteFirmaZeitStart,
  deleteFirmaZeitSucceeded
} from '@src/app/pages/Mitarbeiterverwaltung/store/actions/firmaZeiten-del.actions';
import {
  editFirmaZeitenFailed,
  editFirmaZeitenStart,
  editFirmaZeitenSucceeded
} from '@src/app/pages/Mitarbeiterverwaltung/store/actions/firmaZeiten-edit.actions';
import {
  loadFeiertageFailed,
  loadFeiertageStart,
  loadFeiertageSucceeded
} from '@src/app/pages/Mitarbeiterverwaltung/store/actions/getFeiertage.actions';
import {
  addFeiertageFailed,
  addFeiertageStart,
  addFeiertageSucceeded
} from '@src/app/pages/Mitarbeiterverwaltung/store/actions/feiertage-add.actions';
import {
  deleteFeiertageFailed,
  deleteFeiertageStart,
  deleteFeiertageSucceeded
} from '@src/app/pages/Mitarbeiterverwaltung/store/actions/feiertage-del.actions';
import {
  editFeiertageFailed,
  editFeiertageStart,
  editFeiertageSucceeded
} from '@src/app/pages/Mitarbeiterverwaltung/store/actions/feiertage-edit.actions';

@Injectable()
export class FirmaEffects {
    /**
     * Loads the address list when dispatching the start action
     */
    public loadFirma$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadFirmaStart),
            mergeMap(() =>
                this.firmaService.getFirma().pipe(
                    map((response) => loadFirmaSucceeded({ item: response })),
                    catchError((e) => of(loadFirmaFailed(e))),
                ),
            ),
        ),
    );

  public loadFeiertage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFeiertageStart),
      mergeMap((firmaZeit) =>
        this.firmaService.getFeiertage(firmaZeit.firmaZeitId).pipe(
          map((response) => loadFeiertageSucceeded({ item: response.data })),
          catchError((e) => of(loadFeiertageFailed(e))),
        ),
      ),
    ),
  );

  public loadFirmaZeiten$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFirmaZeitenStart),
      mergeMap(() =>
        this.firmaService.getFirmaZeiten().pipe(
          map((response) => loadFirmaZeitenSucceeded({ item: response.data })),
          catchError((e) => of(loadFirmaZeitenFailed(e))),
        ),
      ),
    ),
  );

  public addFirmaZeit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addFirmaZeitStart),
      mergeMap(() =>
        this.firmaService.addFirmaZeit().pipe(
          tap((item) => sessionStorage.setItem('firmaZeitId', item.toString())),
          mergeMap((response) => of(addFirmaZeitSucceeded({ item: response }))),
          catchError((e) => of(addFirmaZeitFailed(e))),
        ),
      ),
    ),
  );

  public addFeiertage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addFeiertageStart),
      mergeMap((feiertag) =>
        this.firmaService.addFeiertage(feiertag.item).pipe(
          mergeMap((response) => of(addFeiertageSucceeded({ item: response }), loadFeiertageStart({firmaZeitId: response.firmaZeitId}))),
          catchError((e) => of(addFeiertageFailed(e))),
        ),
      ),
    ),
  );

  public editFeiertage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editFeiertageStart),
      mergeMap((feiertag) =>
        this.firmaService.editFeiertage(feiertag.item).pipe(
          mergeMap((response) => of(editFeiertageSucceeded({ item: response }), loadFeiertageStart({firmaZeitId: response.firmaZeitId}))),
          catchError((e) => of(editFeiertageFailed(e))),
        ),
      ),
    ),
  );

  public deleteFeiertage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFeiertageStart),
      mergeMap((feiertag) =>
        this.firmaService.deleteFeiertage(feiertag.item).pipe(
          mergeMap((response) => of(deleteFeiertageSucceeded({ item: response }), loadFeiertageStart({firmaZeitId: response.firmaZeitId}))),
          catchError((e) => of(deleteFeiertageFailed(e))),
        ),
      ),
    ),
  );

  public editFirmaZeit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editFirmaZeitenStart),
      mergeMap((firmaZeit) =>
        this.firmaService.editFirmaZeit(firmaZeit.item).pipe(
          mergeMap((response) => of(editFirmaZeitenSucceeded({ item: response }), loadFirmaZeitenStart())),
          catchError((e) => of(editFirmaZeitenFailed(e))),
        ),
      ),
    ),
  );

  public deleteFirmaZeit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFirmaZeitStart),
      mergeMap((firmaZeitId) =>
        this.firmaService.deleteFirmaZeit(firmaZeitId.item).pipe(
          mergeMap((response) => of(deleteFirmaZeitSucceeded(), loadFirmaZeitenStart())),
          tap(() => sessionStorage.setItem('firmaZeitId', 'F')),
          catchError((e) => of(deleteFirmaZeitFailed(e))),
        ),
      ),
    ),
  );

  public editFirma$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editFirmaStart),
      mergeMap((addressStartAction) =>
        this.firmaService.edit(addressStartAction.item).pipe(
          mergeMap((response) => of(editFirmaSucceeded({ item: response }), loadFirmaStart())),
          catchError((e) => of(editFirmaFailed(e))),
        ),
      ),
    ),
  );


    /**
     * Constructor of address effects
     *
     * @param actions$ - The action observable
     * @param addressesService - The address service
     */
    constructor(private actions$: Actions, private firmaService: FirmaService) {}
}
